<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import Multiselect from 'vue-multiselect'
import * as usersApi from '@/services/rest-api-sms/lib/users/users'
import * as technicianApi from '@/services/rest-api-sms/lib/technician/technician'
import { rateOptions, dayOffReasons, timeOptions } from './constant'

export default {
  page: {
    title: 'Technicians',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    Multiselect,
  },

  data() {
    return {
      technicians: [],
      skills: [],
      dayOffs: [],
      skillRatings: {},
      selectedTechnicianId: null,
      isLoadingTechnicians: false,
      isLoadingSkills: false,
      isLoadingDetail: false,
      savingSkillId: null,
      isSavingDayOff: false,
      deletingDayOffId: null,
      dayOffForm: {
        date: this.toDateInputValue(new Date()),
        is_whole_day: true,
        from: '08:00',
        to: '18:00',
        reason: null,
        other_reason: '',
      },
      rateOptions,
      dayOffReasons,
      timeOptions,
    }
  },

  computed: {
    selectedTechnician: {
      get() {
        return this.technicians.find(
          (technician) => Number(technician.user_id) === Number(this.selectedTechnicianId)
        )
      },
      set(technician) {
        if (technician) this.selectTechnician(technician)
      },
    },

    nextThirtyDays() {
      return Array.from({ length: 30 }, (_, index) => {
        const date = new Date()
        date.setHours(0, 0, 0, 0)
        date.setDate(date.getDate() + index)
        const value = this.toDateInputValue(date)
        const dayOffs = this.dayOffs.filter((dayOff) => this.toDateInputValue(dayOff.date) === value)

        return {
          value,
          title: index === 0 ? 'Today' : index === 1 ? 'Tomorrow' : this.shortWeekday(date),
          date: this.shortDate(date),
          dayOffs,
          isOff: dayOffs.length > 0,
          isWholeDay: dayOffs.some((dayOff) => dayOff.is_whole_day),
        }
      })
    },

    dayOptions() {
      return this.nextThirtyDays.map((day) => ({
        value: day.value,
        text: `${day.title} ${day.date}`,
      }))
    },
  },

  async mounted() {
    await Promise.all([this.fetchTechnicians(), this.fetchSkills()])

    if (this.technicians.length > 0) {
      await this.selectTechnician(this.technicians[0])
    }
  },

  methods: {
    async fetchTechnicians() {
      this.isLoadingTechnicians = true

      try {
        const response = await usersApi.getTechnicians()
        this.technicians = this.normalizeTechnicianList(response)
      } catch (error) {
        console.error(error)
        this.$mcToast.error('Failed to load technicians')
      } finally {
        this.isLoadingTechnicians = false
      }
    },

    async fetchSkills() {
      this.isLoadingSkills = true

      try {
        const response = await technicianApi.getSkills()
        this.skills = this.normalizePaginatedList(response)
      } catch (error) {
        console.error(error)
        this.$mcToast.error('Failed to load technician skills')
      } finally {
        this.isLoadingSkills = false
      }
    },

    async selectTechnician(technician) {
      this.selectedTechnicianId = technician.user_id
      this.skillRatings = this.buildEmptySkillRatings()
      this.dayOffs = []

      await this.fetchTechnicianDetail()
    },

    async fetchTechnicianDetail() {
      if (!this.selectedTechnicianId) return

      this.isLoadingDetail = true

      try {
        await Promise.all([this.fetchTechnicianSkills(), this.fetchDayOffs()])
      } finally {
        this.isLoadingDetail = false
      }
    },

    async fetchTechnicianSkills() {
      try {
        const response = await technicianApi.getTechnicianSkills(this.selectedTechnicianId)
        const savedSkills = this.normalizePaginatedList(response)

        this.skillRatings = savedSkills.reduce((ratings, skill) => {
          const skillId = skill.technician_skill_id || skill.skill_id
          if (skillId) ratings[skillId] = Number(skill.rate || 0) || null
          return ratings
        }, this.buildEmptySkillRatings())
      } catch (error) {
        console.error(error)
        this.$mcToast.error('Failed to load saved skill ratings')
      }
    },

    async fetchDayOffs() {
      if (!this.selectedTechnicianId) return

      try {
        const response = await technicianApi.getDayOffs({
          page: 1,
          limit: 31,
          filters: [
            `date gte '${this.startDateFilter()} 00:00:00'`,
            `date lte '${this.endDateFilter()} 23:59:59'`,
            `user_id eq ${this.selectedTechnicianId}`,
          ],
        })

        this.dayOffs = this.normalizePaginatedList(response)
      } catch (error) {
        console.error(error)
        this.$mcToast.error('Failed to load technician day off')
      }
    },

    async saveSkillRating(skill) {
      const skillId = skill.technician_skill_id
      const rate = this.skillRatings[skillId]

      if (!this.selectedTechnicianId || !skillId || !rate) return

      this.savingSkillId = skillId

      try {
        await technicianApi.saveTechnicianSkill({
          user_id: this.selectedTechnicianId,
          technician_skill_id: skillId,
          rate,
        })
        this.$mcToast.success(`${skill.name} rating saved`)
      } catch (error) {
        console.error(error)
        this.$mcToast.error('Failed to save skill rating')
      } finally {
        this.savingSkillId = null
      }
    },

    async addDayOff() {
      if (!this.selectedTechnicianId || !this.dayOffForm.date || !this.dayOffForm.reason) return

      this.isSavingDayOff = true

      try {
        await technicianApi.createDayOff({
          date: this.toApiDate(this.dayOffForm.date, this.dayOffForm.from),
          is_whole_day: this.dayOffForm.is_whole_day,
          reason:
            this.dayOffForm.reason === 'Other'
              ? this.dayOffForm.other_reason || 'Other'
              : this.dayOffForm.reason,
          user_id: this.selectedTechnicianId,
          from_time: this.dayOffForm.is_whole_day ? undefined : this.dayOffForm.from,
          to_time: this.dayOffForm.is_whole_day ? undefined : this.dayOffForm.to,
        })

        this.$mcToast.success('Day off saved')
        await this.fetchDayOffs()
      } catch (error) {
        console.error(error)
        this.$mcToast.error('Failed to save day off')
      } finally {
        this.isSavingDayOff = false
      }
    },

    async removeDayOff(dayOff) {
      this.deletingDayOffId = dayOff.technician_day_off_id

      try {
        await technicianApi.deleteDayOff(dayOff.technician_day_off_id)
        this.$mcToast.success('Day off removed')
        await this.fetchDayOffs()
      } catch (error) {
        console.error(error)
        this.$mcToast.error('Failed to remove day off')
      } finally {
        this.deletingDayOffId = null
      }
    },

    getTechnicianName(technician) {
      return technician.name
    },

    getTechnicianLabel(technician) {
      if (!technician) return ''

      const label = `${technician?.name} - ${technician?.address?.area}`
      const offCount = this.getTechnicianOffCount(technician)

      return offCount ? `${label} ${offCount} off` : label
    },

    getTechnicianOffCount(technician) {
      if (Number(technician.user_id) !== Number(this.selectedTechnicianId)) return 0

      return this.dayOffs.length
    },

    normalizeTechnicianList(response) {
      if (Array.isArray(response)) return response
      if (Array.isArray(response?.data)) return response.data
      if (Array.isArray(response?.data?.paginated_result)) return response.data.paginated_result
      if (Array.isArray(response?.paginated_result)) return response.paginated_result

      return []
    },

    normalizePaginatedList(response) {
      if (Array.isArray(response)) return response
      if (Array.isArray(response?.data)) return response.data
      if (Array.isArray(response?.data?.paginated_result)) return response.data.paginated_result
      if (Array.isArray(response?.paginated_result)) return response.paginated_result

      return []
    },

    buildEmptySkillRatings() {
      return this.skills.reduce((ratings, skill) => {
        ratings[skill.technician_skill_id] = null
        return ratings
      }, {})
    },

    dayOffWindow(dayOff) {
      if (dayOff.is_whole_day) return 'Whole day'

      const from = dayOff.from_time || this.timeFromDate(dayOff.date)
      const to = dayOff.to_time || '18:00'

      return `${from}-${to}`
    },

    calendarStatus(day) {
      if (!day.isOff) return 'Available'
      if (day.isWholeDay) return 'Whole day'

      return day.dayOffs.map((dayOff) => this.dayOffWindow(dayOff)).join(', ')
    },

    formatShortDate(value) {
      return this.shortDate(new Date(`${value}T00:00:00`))
    },

    shortWeekday(date) {
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    },

    shortDate(date) {
      return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
      })
    },

    timeFromDate(value) {
      if (!value) return '08:00'

      return new Date(value).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    },

    toDateInputValue(value) {
      const date = new Date(value)
      const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)

      return offsetDate.toISOString().slice(0, 10)
    },

    toApiDate(dateValue, timeValue = '08:00') {
      const time = this.dayOffForm.is_whole_day ? '08:00' : timeValue

      return new Date(`${dateValue}T${time}:00Z`).toISOString()
    },

    startDateFilter() {
      return this.toDateInputValue(new Date())
    },

    endDateFilter() {
      const baseDate = new Date()
      baseDate.setHours(0, 0, 0, 0)
      baseDate.setDate(baseDate.getDate() + 30)

      return this.toDateInputValue(baseDate)
    },
  },
}
</script>

<template>
  <Layout>
    <div class="mb-3">
      <PageHeader title="Technicians" />
      <p>
        Set each technician's skill ratings and days off for the next 30 days, including today.
        A technician on a day off is greyed out in the Calendar and cannot be assigned for that window.
      </p>
    </div>

    <h6 class="mt-3">
      {{ isLoadingTechnicians ? 'Loading technicians...' : 'Select Technician' }}
    </h6>

    <multiselect
      class="mb-3"
      v-model="selectedTechnician"
      :options="technicians"
      :placeholder="isLoadingTechnicians ? 'Loading..' : 'Technician'"
      :custom-label="getTechnicianLabel"
      :allow-empty="false"
      track-by="user_id"
      style="max-width: 1000px"
    />

    <div class="technician-planner__grid">
      <div class="technician-planner__main">
        <section class="technician-planner__card">
          <div class="d-flex align-items-center mb-4 gap-2">
            <h2 class="mb-0">{{ this.selectedTechnician?.name }}</h2>
            <span class="technician-planner__tag ml-2">{{ this.selectedTechnician?.address?.area }}</span>
            <b-spinner v-if="isLoadingSkills || isLoadingDetail" small class="ml-auto" />
          </div>

          <b-row>
            <b-col
              v-for="skill in skills"
              :key="skill.technician_skill_id"
              md="3"
              class="mb-3"
            >
              <label
                class="technician-planner__label"
                :for="`skill-${skill.technician_skill_id}`"
              >
                {{ skill.name }} rating
              </label>
              <v-select
                :input-id="`skill-${skill.technician_skill_id}`"
                v-model="skillRatings[skill.technician_skill_id]"
                :options="rateOptions"
                label="text"
                :reduce="(option) => option.value"
                :clearable="false"
                :disabled="savingSkillId === skill.technician_skill_id"
                @input="saveSkillRating(skill)"
              />
            </b-col>
          </b-row>

          <p class="mb-0">
            Location is East or West. Selecting a WO in the Calendar lists only technicians in that WO's region.
            Ratings run from 1 (lowest) to 5 (best). The Calendar rating filters and the primary-technician skill check use them.
            Changing a rating does not unassign existing WOs.
          </p>
        </section>

        <section class="technician-planner__card">
          <h2>Add a day off</h2>
          <b-form @submit.prevent="addDayOff">
            <b-row>
              <b-col md="4" class="mb-3">
                <label class="technician-planner__label" for="day-off-date">Day</label>
                <v-select
                  input-id="day-off-date"
                  v-model="dayOffForm.date"
                  :options="dayOptions"
                  label="text"
                  :reduce="(option) => option.value"
                  :clearable="false"
                  :disabled="!selectedTechnician || isSavingDayOff"
                  @input="fetchDayOffs"
                />
              </b-col>

              <b-col md="4" class="mb-3">
                <label class="technician-planner__label">Time</label>
                <b-form-checkbox
                  v-model="dayOffForm.is_whole_day"
                  :disabled="!selectedTechnician || isSavingDayOff"
                >
                  <span class="ml-2">Whole day</span>
                </b-form-checkbox>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="4" class="mb-3">
                <label class="technician-planner__label" for="day-off-from">From</label>
                <v-select
                  input-id="day-off-from"
                  v-model="dayOffForm.from"
                  :options="timeOptions"
                  :clearable="false"
                  :disabled="dayOffForm.is_whole_day || !selectedTechnician || isSavingDayOff"
                />
              </b-col>

              <b-col md="4" class="mb-3">
                <label class="technician-planner__label" for="day-off-to">To</label>
                <v-select
                  input-id="day-off-to"
                  v-model="dayOffForm.to"
                  :options="timeOptions"
                  :clearable="false"
                  :disabled="dayOffForm.is_whole_day || !selectedTechnician || isSavingDayOff"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col md="4" class="mb-3">
                <label class="technician-planner__label" for="day-off-reason">Reason</label>
                <v-select
                  input-id="day-off-reason"
                  v-model="dayOffForm.reason"
                  :options="dayOffReasons"
                  label="text"
                  :reduce="(option) => option.value"
                  :clearable="false"
                  :disabled="!selectedTechnician || isSavingDayOff"
                />
              </b-col>

              <b-col v-if="dayOffForm.reason === 'Other'" md="4" class="mb-3">
                <label class="technician-planner__label" for="day-off-other">If other, describe</label>
                <b-form-input
                  id="day-off-other"
                  v-model="dayOffForm.other_reason"
                />
              </b-col>
            </b-row>

            <b-button
              variant="primary"
              type="submit"
              :disabled="!selectedTechnician || isSavingDayOff || !dayOffForm.reason"
            >
              <b-spinner v-if="isSavingDayOff" small class="mr-1" />
              Add day off
            </b-button>
          </b-form>

          <p class="mb-0 mt-3">
            Whole day covers 00:00 to 24:00. Untick it to pick a start and end time on a 24-hour clock.
            The Calendar greys out the same hours. A day off cannot be saved while the technician has a Scheduled or Installing WO in that window.
          </p>
        </section>
      </div>

      <aside class="technician-planner__side">
        <section class="technician-planner__card">
          <h2>Days off</h2>
          <div v-if="dayOffs.length === 0" class="technician-planner__empty">
            No days off in the next 30 days.
          </div>

          <div
            v-for="dayOff in dayOffs"
            :key="dayOff.technician_day_off_id"
            class="technician-planner__off-row"
          >
            <div>
              <strong>{{ formatShortDate(toDateInputValue(dayOff.date)) }} · {{ dayOffWindow(dayOff) }}</strong>
              <span>{{ dayOff.reason }}</span>
            </div>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="deletingDayOffId === dayOff.technician_day_off_id"
              @click="removeDayOff(dayOff)"
            >
              <b-spinner v-if="deletingDayOffId === dayOff.technician_day_off_id" small />
              <span v-else>Remove</span>
            </b-button>
          </div>
        </section>

        <section class="technician-planner__card">
          <h2>Next 30 days</h2>
          <div class="technician-planner__calendar">
            <button
              v-for="day in nextThirtyDays"
              :key="day.value"
              type="button"
              class="technician-planner__day"
              :class="{
                'technician-planner__day--selected': day.value === dayOffForm.date,
                'technician-planner__day--off': day.isOff,
              }"
              @click="dayOffForm.date = day.value"
            >
              <strong>{{ day.title }}<br>{{ day.date }}</strong>
              <span>{{ calendarStatus(day) }}</span>
            </button>
          </div>
        </section>
      </aside>
    </div>
  </Layout>
</template>

<style scoped>
.technician-planner__tag {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  color: #344054;
  background: #eef2f6;
  font-size: 13px;
  font-weight: 700;
}

.technician-planner__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 24px;
}

.technician-planner__main,
.technician-planner__side {
  display: grid;
  gap: 24px;
  align-content: start;
}

.technician-planner__card {
  padding: 24px;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
}

.technician-planner__card h2 {
  margin: 0 0 18px;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
}

.technician-planner__label {
  display: block;
  margin-bottom: 8px;
  color: #667085;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .02em;
  text-transform: uppercase;
}

.technician-planner__off-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #eef2f6;
}

.technician-planner__off-row strong,
.technician-planner__off-row span {
  display: block;
}

.technician-planner__off-row span {
  margin-top: 4px;
  color: #667085;
}

.technician-planner__calendar {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.technician-planner__day {
  min-height: 86px;
  padding: 10px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  color: #344054;
  background: #fff;
  text-align: left;
}

.technician-planner__day strong,
.technician-planner__day span {
  display: block;
}

.technician-planner__day span {
  margin-top: 6px;
  color: #667085;
  font-size: 13px;
}

.technician-planner__day--selected {
  border-color: #2f80ed;
  box-shadow: 0 0 0 1px #2f80ed;
}

.technician-planner__day--off {
  background:
    repeating-linear-gradient(
      -45deg,
      rgba(47, 128, 237, .08),
      rgba(47, 128, 237, .08) 8px,
      transparent 8px,
      transparent 16px
    ),
    #f8fbff;
}

.technician-planner::v-deep .custom-select,
.technician-planner::v-deep .form-control {
  min-height: 52px;
  border-color: #d0d5dd;
  border-radius: 8px;
  color: #344054;
  background-color: #fff;
}

.technician-planner::v-deep .custom-select:disabled,
.technician-planner::v-deep .form-control:disabled {
  color: #98a2b3;
  background-color: #f9fafb;
}

.technician-planner::v-deep .custom-control-label {
  padding-top: 2px;
}

@media (max-width: 1199.98px) {
  .technician-planner__grid {
    grid-template-columns: 1fr;
  }

  .technician-planner__side {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767.98px) {
  .technician-planner {
    margin: -16px;
    padding: 24px 16px;
  }

  .technician-planner__side {
    grid-template-columns: 1fr;
  }

  .technician-planner__calendar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

::v-deep .custom-control-label {
  padding-left: 0.5rem; /* Adjust space as needed */
}
</style>
